<script setup>
import { ref, computed, onMounted } from 'vue'
import D3DonutChart from '@/components/charts/D3DonutChart.vue'
import api from '@/api'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const props = defineProps({
  // Current table filter state, keyed by dimension — marks the active slice
  activeFilters: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['filter'])

const loading = ref(true)
const error   = ref('')
const stats   = ref(null)

// Stable semantic colors for fixed-value dimensions
const VIS_COLORS = { community: '#22c55e', company: '#8b5cf6', public: '#06b6d4' }
const SEV_COLORS = { CRITICAL: '#dc2626', HIGH: '#f97316', MEDIUM: '#eab308', LOW: '#3b82f6', INFO: '#64748b' }
const SEV_ORDER  = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO']

async function fetchStats() {
  loading.value = true
  error.value   = ''
  try {
    const { data: res } = await api.getWorkflowsStats()
    stats.value = res?.data ?? null
  } catch {
    error.value = t('siemWorkflows.statsLoadFailed')
  } finally {
    loading.value = false
  }
}

const visibilityData = computed(() => (stats.value?.visibility ?? []).map(r => {
  const key = r.name ?? 'community'
  const label = { community: t('siemWorkflows.visCommunity'), company: t('siemWorkflows.visCompany'), public: t('siemWorkflows.visPublic') }[key] ?? key
  return { label, value: r.count, color: VIS_COLORS[key], raw: key }
}))

const severityData = computed(() => [...(stats.value?.severity ?? [])]
  .sort((a, b) => SEV_ORDER.indexOf(a.name) - SEV_ORDER.indexOf(b.name))
  .map(r => ({
    label: r.name ?? t('siemWorkflows.statsNone'),
    value: r.count,
    color: SEV_COLORS[r.name] ?? '#94a3b8',
    raw:   r.name ?? null, // null = not filterable
  })))

const mapNamed = rows => (rows ?? []).map(r => ({
  label: r.name,
  value: r.count,
  raw:   r.name ?? null,
}))

const mitreData = computed(() => mapNamed(stats.value?.mitre))
const isacaData = computed(() => mapNamed(stats.value?.isaca))
const tagsData  = computed(() => mapNamed(stats.value?.tags))

const widgets = computed(() => [
  { key: 'visibility', dimension: 'visibility', icon: 'pi pi-eye',   title: t('siemWorkflows.statsVisibility'), data: visibilityData.value },
  { key: 'severity',   dimension: 'severity',   icon: 'pi pi-bolt',  title: t('siemWorkflows.statsSeverity'),   data: severityData.value },
  { key: 'mitre',      dimension: 'mitre',      icon: 'pi pi-table', title: t('siemWorkflows.statsMitre'),      data: mitreData.value },
  { key: 'isaca',      dimension: 'isaca',      icon: 'pi pi-book',  title: t('siemWorkflows.statsIsaca'),      data: isacaData.value },
  { key: 'tags',       dimension: 'tag',        icon: 'pi pi-tags',  title: t('siemWorkflows.statsTags'),       data: tagsData.value },
])

function onSliceClick(dimension, item) {
  if (item.isOther || item.raw == null) return
  emit('filter', { dimension, value: item.raw })
}

onMounted(fetchStats)
defineExpose({ refresh: fetchStats })
</script>

<template>
  <div class="workflows-stats-panel">
    <div v-for="w in widgets" :key="w.key" class="hw-card">
      <div class="hw-card-header">
        <i :class="w.icon" class="hw-card-icon" />
        <span class="hw-card-title">{{ w.title }}</span>
      </div>
      <div v-if="loading" class="hw-card-loading"><i class="pi pi-spin pi-spinner" /></div>
      <div v-else class="hw-card-body">
        <D3DonutChart
          :data="w.data"
          :other-label="t('siemWorkflows.statsOther')"
          :selected="props.activeFilters?.[w.dimension] ?? null"
          @slice-click="item => onSliceClick(w.dimension, item)"
        />
      </div>
      <div v-if="!loading && error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>
    </div>
  </div>
</template>

<style scoped>
.workflows-stats-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
</style>
